import React from "react";
import {
  Box, TextField, FormControlLabel, Switch, Typography, IconButton, Button,
  Select, MenuItem, FormControl, InputLabel, Chip, Alert, Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { FIELD_TYPE_MAP, genId, CLINICAL_KEYS, findReferences, LAYOUT_FIELD_TYPES, updateAt } from "../formsUtils";
import { OPERATOR_LABELS } from "../engine/logicEngine";
import ConditionBuilder from "./ConditionBuilder";

/** Paleta do módulo. Os componentes usam estes nomes, nunca o hexadecimal solto. */
export const UI = {
  primary: "#3f51b5",
  primarySoft: "#eceefa",
  primaryBorder: "#c9cfee",
  surface: "#ffffff",
  sunken: "#f7f8fa",
  borderSubtle: "#ededf0",
  textBody: "#3d3d40",
  textMuted: "#6b6c72",
  radius: 10,
};

/** Bloco de um assunto. Rótulo overline + corpo, com a mesma respiração em todos. */
function Section({ title, hint, action, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: hint ? 0.25 : 1, minHeight: 28 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
          color: UI.textMuted, flex: 1,
        }}>
          {title}
        </Typography>
        {action}
      </Box>
      {hint && (
        <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", mb: 1 }}>
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

/**
 * Descreve a condição em português, uma linha por regra, para conferir sem ler o JSON.
 * Devolve { conector, linhas: [{ pergunta, condicao, resposta }] } ou null.
 */
export function describeCondition(cond, fields) {
  if (!cond) return null;
  const achar = (ref) => (fields || []).find((x) => x.id === ref || x.name === ref);
  const daRegra = (r) => {
    if (!r || Array.isArray(r.rules)) return { pergunta: "(subgrupo de condições)", condicao: "", resposta: "" };
    if (!r.field) return { pergunta: "(regra incompleta)", condicao: "", resposta: "" };
    const f = achar(r.field);
    const rotulo = (v) => ((f && f.options && f.options.find((o) => o.value === v)) || {}).label || v;
    const valores = Array.isArray(r.value) ? r.value : [r.value];
    const resposta = valores.filter((v) => v !== undefined && v !== null && String(v) !== "").map(rotulo).join(", ");
    return {
      pergunta: f ? (f.label || f.name || f.id) : r.field,
      condicao: (OPERATOR_LABELS && OPERATOR_LABELS[r.operator]) || r.operator,
      resposta,
    };
  };
  const regras = Array.isArray(cond.rules) ? cond.rules : [cond];
  if (regras.length === 0) return null;
  return { conector: cond.op === "or" ? "OU" : "E", linhas: regras.map(daRegra) };
}

/** Editor do campo selecionado. Renderizado dentro do modal do construtor. */
export default function FieldInspector({ field, allFields, definition, onChange }) {
  if (!field) return null;

  const set = (patch) => onChange({ ...field, ...patch });
  const setValidation = (patch) => onChange({ ...field, validation: { ...(field.validation || {}), ...patch } });
  const isLayout = LAYOUT_FIELD_TYPES.includes(field.type);
  const isCover = field.type === "cover";
  const isConsent = field.type === "consent";
  const isDisplay = isLayout || isCover || isConsent;

  // outros campos disponíveis para condição (exclui o próprio)
  const otherFields = (allFields || []).filter((f) => f.id !== field.id);

  // Renomear não reescreve quem cita o nome antigo, e a referência quebrada resolve para 0
  // em silêncio (muda a faixa do escore sem aviso). Enquanto houver referência, o rename fica
  // travado: o autor ajusta as regras primeiro.
  const nameRefs = field.name ? findReferences(definition || { fields: allFields }, [field.name]) : [];

  const resumoCondicao = describeCondition(field.visibleIf, otherFields);

  return (
    <Box>
      <Section title={isDisplay ? "Conteúdo" : "Pergunta"}>
        <TextField fullWidth size="small"
          label={isCover || isConsent ? "Título" : isLayout ? "Texto" : "Rótulo"}
          value={field.label || ""} onChange={(e) => set({ label: e.target.value })}
          multiline={isLayout} />

        {(isCover || isConsent) && (
          <>
            <TextField fullWidth size="small" sx={{ mt: 2 }} multiline minRows={isConsent ? 5 : 3}
              label={isConsent ? "Texto dos termos" : "Texto"}
              value={field.body || ""} onChange={(e) => set({ body: e.target.value })}
              helperText="Aparece abaixo do título" />
            <TextField fullWidth size="small" sx={{ mt: 2 }} label="Rótulo do botão (avançar)"
              value={field.buttonLabel || ""} onChange={(e) => set({ buttonLabel: e.target.value })}
              placeholder="Próximo" />
            {isConsent && (
              <TextField fullWidth size="small" sx={{ mt: 2 }} label="Rótulo do checkbox"
                value={field.checkboxLabel || ""} onChange={(e) => set({ checkboxLabel: e.target.value })} />
            )}
          </>
        )}

        {!isDisplay && (
          <>
            <TextField fullWidth size="small" sx={{ mt: 2 }} label="Texto de ajuda"
              value={field.helpText || ""} onChange={(e) => set({ helpText: e.target.value })}
              helperText="Linha menor, abaixo da pergunta" />
            {["text", "textarea", "number", "email", "phone"].includes(field.type) && (
              <TextField fullWidth size="small" sx={{ mt: 2 }} label="Placeholder"
                value={field.placeholder || ""} onChange={(e) => set({ placeholder: e.target.value })} />
            )}
          </>
        )}
      </Section>

      {Array.isArray(field.options) && <OptionsEditor field={field} onChange={onChange} />}

      {(!isDisplay || isConsent) && (
        <Section title="Comportamento">
          <Box sx={{
            border: `1px solid ${UI.borderSubtle}`, borderRadius: `${UI.radius}px`,
            background: UI.sunken, px: 2,
          }}>
            <FormControlLabel sx={{ m: 0, py: 1, display: "flex", justifyContent: "space-between", width: "100%" }}
              labelPlacement="start"
              control={<Switch checked={!!field.required} onChange={(e) => set({ required: e.target.checked })} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: UI.textBody }}>Obrigatório</Typography>
                  <Typography variant="caption" sx={{ color: UI.textMuted }}>
                    O paciente não avança sem responder
                  </Typography>
                </Box>
              } />
            {field.clinicalKey === "cpf" && (
              <FormControlLabel sx={{
                m: 0, py: 1, display: "flex", justifyContent: "space-between", width: "100%",
                borderTop: `1px solid ${UI.borderSubtle}`,
              }}
                labelPlacement="start"
                control={<Switch checked={!!field.allowForeignCpf}
                  onChange={(e) => set({ allowForeignCpf: e.target.checked })} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: UI.textBody }}>Permitir estrangeiro</Typography>
                    <Typography variant="caption" sx={{ color: UI.textMuted }}>
                      RNE ou passaporte, mais a nacionalidade
                    </Typography>
                  </Box>
                } />
            )}
          </Box>
        </Section>
      )}

      {!isDisplay && (
        <Section title="Identificação e uso clínico">
          <TextField fullWidth size="small" label="Nome interno"
            value={field.name || ""} disabled={nameRefs.length > 0}
            onChange={(e) => set({ name: e.target.value.replace(/[^A-Za-z0-9_]/g, "_") })}
            helperText={nameRefs.length > 0
              ? `Bloqueado: ${nameRefs.length} referência(s) a "${field.name}" (${nameRefs.join("; ")}). Ajuste-as antes de renomear.`
              : "Usado em cálculos e condições. Apenas letras, números e _"} />

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Chave clínica (APA)</InputLabel>
            <Select label="Chave clínica (APA)" value={field.clinicalKey || ""}
              onChange={(e) => set({ clinicalKey: e.target.value || undefined })}>
              {CLINICAL_KEYS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: UI.textMuted, mt: 0.5, display: "block" }}>
            Diz ao sistema o que este campo é. É o que liga a máscara, o cálculo e o envio ao APA legado.
          </Typography>
        </Section>
      )}

      {!isDisplay && (
        <Section title="Validação" hint="Deixe vazio para não restringir.">
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {["text", "textarea"].includes(field.type) && (
              <>
                <TextField size="small" type="number" label="Mín. caracteres" sx={{ flex: "1 1 140px" }}
                  value={field.validation?.minLength ?? ""} onChange={(e) => setValidation({ minLength: e.target.value })} />
                <TextField size="small" type="number" label="Máx. caracteres" sx={{ flex: "1 1 140px" }}
                  value={field.validation?.maxLength ?? ""} onChange={(e) => setValidation({ maxLength: e.target.value })} />
              </>
            )}
            {field.type === "number" && (
              <>
                <TextField size="small" type="number" label="Valor mín." sx={{ flex: "1 1 140px" }}
                  value={field.validation?.min ?? ""} onChange={(e) => setValidation({ min: e.target.value })} />
                <TextField size="small" type="number" label="Valor máx." sx={{ flex: "1 1 140px" }}
                  value={field.validation?.max ?? ""} onChange={(e) => setValidation({ max: e.target.value })} />
              </>
            )}
            {field.type === "date" && (
              <>
                <TextField size="small" label="Data mín. (aaaa-mm-dd)" sx={{ flex: "1 1 140px" }}
                  value={field.validation?.min ?? ""} onChange={(e) => setValidation({ min: e.target.value })} />
                <TextField size="small" label='Data máx. (aaaa-mm-dd ou "hoje")' sx={{ flex: "1 1 140px" }}
                  value={field.validation?.max ?? ""} onChange={(e) => setValidation({ max: e.target.value })} />
              </>
            )}
            <TextField size="small" label="Regex (avançado)" sx={{ flex: "1 1 100%" }}
              value={field.validation?.pattern ?? ""} onChange={(e) => setValidation({ pattern: e.target.value })} />
          </Box>
        </Section>
      )}

      <Section
        title="Visibilidade condicional"
        hint="Sem condição, a pergunta aparece para todos."
        action={field.visibleIf
          ? <Chip size="small" label="Condicional" sx={{
              height: 20, fontSize: 11, fontWeight: 600,
              color: UI.primary, background: UI.primarySoft, border: `1px solid ${UI.primaryBorder}`,
            }} />
          : null}
      >
        {resumoCondicao && (
          <Alert severity="info" icon={false} sx={{
            mb: 1.5, py: 0.5, borderRadius: `${UI.radius}px`,
            background: UI.primarySoft, border: `1px solid ${UI.primaryBorder}`, color: UI.textBody,
          }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", color: UI.textMuted, mb: 0.5 }}>
              Aparece quando
            </Typography>
            {resumoCondicao.linhas.map((l, i) => (
              <Typography key={i} variant="body2" sx={{ mt: i ? 0.5 : 0 }}>
                {i > 0 && (
                  <Box component="span" sx={{ fontWeight: 700, color: UI.primary, mr: 0.75 }}>
                    {resumoCondicao.conector}
                  </Box>
                )}
                <Box component="span" sx={{ fontWeight: 600 }}>{l.pergunta}</Box>
                {l.condicao && ` ${l.condicao}`}
                {l.resposta && <Box component="span" sx={{ fontWeight: 600 }}>{` ${l.resposta}`}</Box>}
              </Typography>
            ))}
          </Alert>
        )}
        <ConditionBuilder condition={field.visibleIf} fields={otherFields}
          onChange={(cond) => set({ visibleIf: cond })} />
      </Section>
    </Box>
  );
}

function OptionsEditor({ field, onChange }) {
  const options = field.options || [];
  const set = (next) => onChange({ ...field, options: next });

  const updateOpt = (idx, patch) => set(updateAt(options, idx, patch));
  const removeOpt = (idx) => set(options.filter((_, i) => i !== idx));
  const addOpt = () => set([...options, { value: genId("opt"), label: `Opção ${options.length + 1}` }]);

  // peso/score disponível em qualquer campo de opções (usado por escore/agregação)
  const showScore = true;
  const showSuspension = field.clinicalKey === "medications";

  return (
    <Section
      title={`Opções (${options.length})`}
      action={<Button size="small" startIcon={<AddIcon />} onClick={addOpt}>Adicionar</Button>}
    >
      {options.length === 0 && (
        <Typography variant="body2" sx={{ color: UI.textMuted }}>
          Nenhuma opção. O paciente não terá o que escolher.
        </Typography>
      )}
      {options.map((opt, idx) => (
        <Box key={idx} sx={{
          display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap",
          border: `1px solid ${UI.borderSubtle}`, borderLeft: `3px solid ${UI.primaryBorder}`,
          borderRadius: `${UI.radius}px`, background: UI.surface, p: 1.5, mb: 1,
        }}>
          <TextField size="small" label="Rótulo" value={opt.label}
            onChange={(e) => updateOpt(idx, { label: e.target.value })}
            sx={{ flex: "2 1 220px" }} helperText="O que o paciente lê" />
          <TextField size="small" label="Valor" value={opt.value}
            onChange={(e) => updateOpt(idx, { value: e.target.value })}
            sx={{ flex: "1 1 120px" }} helperText="Usado em regras" />
          {showScore && (
            <Tooltip title="Peso de risco desta opção, de 1 a 5. Entra nas agregações do escore.">
              <TextField size="small" type="number" label="Peso" value={opt.score ?? ""}
                onChange={(e) => updateOpt(idx, { score: e.target.value })}
                sx={{ width: 84 }} helperText="1-5" />
            </Tooltip>
          )}
          {showSuspension && (
            <TextField size="small" type="number" label="Susp." value={opt.suspensionDays ?? ""}
              onChange={(e) => updateOpt(idx, { suspensionDays: e.target.value })}
              sx={{ width: 96 }} helperText="dias" />
          )}
          <IconButton size="small" color="error" onClick={() => removeOpt(idx)} sx={{ mt: 0.5 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Section>
  );
}
